import React from 'react';
import { useNavigate } from 'react-router-dom';
import img1 from '../../assets/img1.jpeg';
import { FaCheckCircle, FaPhoneAlt, FaArrowLeft } from 'react-icons/fa';

const getServiceDetails = (title) => {
    switch (title.toLowerCase()) {
        case 'photography':
            return {
                tagline: 'Preserving Your Beautiful Moments',
                desc: 'Capturing the precious highlights of your special day. Our elite photographers use state-of-the-art equipment to capture authentic emotions, exquisite details, and timeless memories that you will cherish forever.',
                features: ['Candid & Traditional Photography', 'Cinematic Wedding Films', 'Pre-Wedding Shoots', 'High-Resolution Albums']
            };
        case 'make up':
        case 'makeup':
            return {
                tagline: 'Flawless Bridal Transformations',
                desc: 'Enhancing your natural beauty for your dream day. Our professional makeup artists specialize in elegant bridal, engagement, haldi, and reception looks, customized to complement your unique style and attire.',
                features: ['Professional HD & Airbrush Makeup', 'Hairstyling & Saree Draping', 'Trial Sessions Available', 'Premium International Brands']
            };
        case 'embellishment & adornment':
        case 'embellishment':
            return {
                tagline: 'Spectacular Venue Styling',
                desc: 'Transforming wedding venues into magical, romantic spaces. From breath-taking floral arrangements to customized traditional themes, we design layouts that add a perfect touch of elegance and grandeur.',
                features: ['Theme-based Stage Decor', 'Exquisite Floral Styling', 'Pathway & Entrance Decor', 'Customized Lighting Concepts']
            };
        case 'logistics':
            return {
                tagline: 'Seamless Travel & Hospitality',
                desc: 'Enjoy stress-free travel and flawless coordination. We manage all logistics, guest hospitality, transportation, and coordination details so you and your loved ones can focus entirely on celebrating.',
                features: ['Luxury Wedding Car Rental', 'Guest Transport Management', 'Coordination & Scheduling', 'Luggage & Route Assistance']
            };
        case 'aerial tour or air excursion':
        case 'aerial tour':
            return {
                tagline: 'Grand Entries & Aerial Views',
                desc: 'Create an unforgettable impact with premium aerial services. From grand helicopter entries for the bride and groom to spectacular drone coverage, make your special event a talking point.',
                features: ['Helicopter Bride/Groom Entry', 'Flower Showering from Skies', 'Cinematic Drone Videography', 'Exclusive Aerial Tours']
            };
        case 'event food & dining':
        case 'event food':
            return {
                tagline: 'Exquisite Culinary Journeys',
                desc: 'Celebrate your union with a feast of delicious flavors. We curate customized menus featuring traditional Maharashtrian delicacies alongside contemporary global cuisines, all prepared by master chefs.',
                features: ['Customized Menu Planning', 'Live Food Counters', 'Premium Buffet & Table Service', 'Hygiene & Quality Assured']
            };
        case 'policies':
        case 'policy':
            return {
                tagline: 'Platform Rules & Safety Policies',
                desc: 'Ensuring a secure, trustworthy, and respectful matrimony experience. We maintain strict compliance policies and community guidelines to safeguard your personal journey and matches.',
                features: ['100% Profile Verification Policy', 'Strict Anti-Harassment Rules', 'Clear Privacy & Consent Controls', 'Refund & Package Guidelines']
            };
        default:
            return {
                tagline: 'Professional Wedding Services',
                desc: `Welcome to the ${title} service page. We provide exceptional, high-quality services tailored to make your wedding day extra special and completely stress-free.`,
                features: ['Tailored Packages', 'Experienced Professionals', '24/7 Customer Support', 'Seamless Execution']
            };
    }
};

const ServicePage = ({ title, image }) => {
    const navigate = useNavigate();
    const serviceDetails = getServiceDetails(title);
    
    return (
        <div style={{ fontFamily: 'Arial, sans-serif', lineHeight: 1.6, width: '100%', overflowX: 'hidden' }}>
            <style>{`
                .service-section {
                    padding: 9.5rem 2rem 4rem;
                    min-height: 90vh;
                    background: linear-gradient(135deg, #fff5f5 0%, #f0f4ff 50%, #f3e8ff 100%);
                    display: flex;
                    align-items: center;
                }
                
                .service-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    width: 100%;
                }
                
                .service-header-wrapper {
                    text-align: center;
                    margin-bottom: 3rem;
                }
                
                .service-tagline {
                    display: inline-block;
                    padding: 0.4rem 1.2rem;
                    background: linear-gradient(135deg, rgba(106, 17, 203, 0.08) 0%, rgba(37, 117, 252, 0.08) 100%);
                    color: #6a11cb;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 1.5px;
                    margin-bottom: 0.75rem;
                    border: 1px solid rgba(106, 17, 203, 0.15);
                }
                
                .service-title {
                    font-size: 3rem;
                    font-weight: 800;
                    color: #1e293b;
                    margin: 0;
                    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                
                .service-columns-container {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 4rem;
                }
                
                .service-info-column {
                    flex: 1.2;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                }
                
                .service-image-column {
                    flex: 1;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                
                .service-image {
                    width: auto;
                    height: auto;
                    max-width: 100%;
                    max-height: 70vh;
                    border-radius: 20px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
                    border: 4px solid white;
                    transition: all 0.3s ease;
                    display: block;
                }
                
                .service-image:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 25px 50px rgba(106, 17, 203, 0.15);
                }
                
                .service-desc {
                    font-size: 1.2rem;
                    color: #475569;
                    line-height: 1.8;
                    margin-bottom: 2rem;
                    text-align: left;
                }
                
                .service-features-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                    margin-bottom: 2.5rem;
                    width: 100%;
                }
                
                .feature-item {
                    display: flex;
                    align-items: center;
                    gap: 0.8rem;
                    font-size: 1.1rem;
                    color: #334155;
                    font-weight: 500;
                }
                
                .feature-icon {
                    color: #10b981;
                    font-size: 1.2rem;
                    flex-shrink: 0;
                }
                
                .cta-button {
                    background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
                    color: white;
                    border: none;
                    padding: 1rem 2.5rem;
                    border-radius: 30px;
                    font-weight: 700;
                    font-size: 1.1rem;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    box-shadow: 0 8px 25px rgba(106, 17, 203, 0.25);
                    display: inline-flex;
                    align-items: center;
                    gap: 0.8rem;
                }
                
                .cta-button:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 30px rgba(106, 17, 203, 0.35);
                }
                
                @media (max-width: 992px) {
                    .service-columns-container {
                        gap: 2rem;
                    }
                    .service-title {
                        font-size: 2.5rem;
                    }
                }
                
                @media (max-width: 768px) {
                    .service-section {
                        padding: 8.5rem 1.25rem 4rem;
                        display: block;
                    }
                    .service-header-wrapper {
                        margin-bottom: 1.5rem;
                        padding: 0 1.5rem;
                    }
                    .service-columns-container {
                        display: flex;
                        flex-direction: column;
                        gap: 2.5rem;
                    }
                    .service-info-column {
                        align-items: center;
                        padding: 0 1.5rem;
                        text-align: center;
                    }
                    .service-desc {
                        text-align: center;
                        font-size: 1.1rem;
                        margin-bottom: 1.5rem;
                    }
                    .service-features-list {
                        align-items: flex-start;
                        max-width: 350px;
                        margin: 0 auto 2rem;
                    }
                    .service-image-column {
                        width: 100%;
                    }
                    .service-image {
                        width: 100% !important;
                        height: auto !important;
                        max-width: 100% !important;
                        max-height: none !important;
                        border-radius: 12px;
                        border: 3px solid white;
                        box-shadow: 0 10px 25px rgba(0,0,0,0.08);
                        display: block;
                    }
                    .cta-button {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>
            
            <header style={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(12px)',
                color: '#1e293b',
                padding: '0.5rem 0',
                position: 'fixed',
                top: 0,
                width: '100%',
                zIndex: 1000,
                boxShadow: '0 2px 20px rgba(0,0,0,0.05)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.4)'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 2rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer' }} onClick={() => navigate('/')}>
                        <img src={img1} alt="Swrajya Sangam Logo" style={{ width: '45px', height: '45px', borderRadius: '8px', objectFit: 'cover' }} />
                        <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 'bold', background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Swrajya Sangam</h1>
                    </div>
                    <button 
                        onClick={() => navigate(-1)} 
                        style={{ 
                            background: 'none', 
                            border: '1px solid #6a11cb', 
                            color: '#6a11cb',
                            padding: '0.5rem 1.2rem', 
                            borderRadius: '20px', 
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = '#6a11cb';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.color = '#6a11cb';
                        }}
                    >
                        <FaArrowLeft /> Back
                    </button>
                </div>
            </header>

            <section className="service-section">
                <div className="service-container">
                    <div className="service-header-wrapper">
                        <span className="service-tagline">{serviceDetails.tagline}</span>
                        <h2 className="service-title">{title}</h2>
                    </div>
                    
                    <div className="service-columns-container">
                        <div className="service-info-column">
                            <p className="service-desc">{serviceDetails.desc}</p>
                            
                            <div className="service-features-list">
                                {serviceDetails.features.map((feature, index) => (
                                    <div key={index} className="feature-item">
                                        <FaCheckCircle className="feature-icon" />
                                        <span>{feature}</span>
                                    </div>
                                ))}
                            </div>
                            
                            <button className="cta-button" onClick={() => navigate('/contact')}>
                                <FaPhoneAlt /> Book Service / Contact Us
                            </button>
                        </div>
                        
                        {image && (
                            <div className="service-image-column">
                                <img src={image} alt={title} className="service-image" />
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServicePage;
